import { createFileRoute } from '@tanstack/react-router'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { useMyProfile, useMySubscription, type SubscriptionTier } from '@/features/home/queries'
import { AccountTabs } from '@/features/profile/components/AccountTabs'
import { PlansGrid } from '@/features/profile/components/PlansGrid'
import styles from './subscription.module.css'

export const Route = createFileRoute('/_authenticated/subscription')({
  component: SubscriptionPage,
})

type Hero = { name: string; price: string; per: string; sub: string }

const HERO: Record<SubscriptionTier, Hero> = {
  BETA: {
    name: 'Beta', price: 'R$ 0', per: 'grátis no beta',
    sub: 'Durante a temporada de lançamento da Copa de Seleções 2026 você tem acesso liberado a todos os recursos — ligas ilimitadas, formatos e placar ao vivo, sem custo.',
  },
  FREE: {
    name: 'Gratuito', price: 'R$ 0', per: '/sempre',
    sub: 'Jogue com os amigos numa liga privada, com escalação e placar ao vivo.',
  },
  ORGANIZADOR: {
    name: 'Organizador', price: 'R$ 19', per: '/mês',
    sub: 'Até 5 ligas, 100 participantes, Head-to-Head e analytics avançado.',
  },
  LIGA_PRO: {
    name: 'Liga Pro', price: 'R$ 49', per: '/mês',
    sub: 'Ligas pagas com prize pool, recebimento via Pix e recursos avançados.',
  },
}

function SubscriptionPage() {
  const { data: profile } = useMyProfile()
  const { data: sub } = useMySubscription()

  const tier = sub?.tier ?? profile?.subscriptionTier ?? 'BETA'
  const hero = HERO[tier]
  const limitLabel = sub ? (sub.leagueLimit === null ? 'ilimitadas' : String(sub.leagueLimit)) : '—'

  return (
    <>
      <AppHeader title="Plano" />
      <div className={styles.page}>
        <AccountTabs />

        {/* Current plan hero */}
        <section className={styles.hero}>
          <div className={styles.heroTop}>
            <div>
              <div className={styles.eyebrow}>Plano atual</div>
              <div className={styles.heroName}>{hero.name}</div>
              <p className={styles.heroSub}>{hero.sub}</p>
            </div>
            <div className={styles.heroPrice}>
              <div className={styles.heroVal}>{hero.price}</div>
              <div className={styles.heroPer}>{hero.per}</div>
            </div>
          </div>
        </section>

        {/* Usage */}
        <div className={styles.usageGrid}>
          <div className={styles.usageCard}>
            <div className={styles.usageK}>Ligas criadas</div>
            <div className={styles.usageV}>{sub?.leaguesCreated ?? '—'} <span className={styles.of}>/ {limitLabel}</span></div>
          </div>
          <div className={styles.usageCard}>
            <div className={styles.usageK}>Participações</div>
            <div className={styles.usageV}>{sub?.leaguesJoined ?? '—'} <span className={styles.of}>ligas</span></div>
          </div>
          <div className={styles.usageCard}>
            <div className={styles.usageK}>Pagamento</div>
            <div className={styles.usageV}>Pix <span className={styles.of}>em breve</span></div>
          </div>
        </div>

        {/* Plans */}
        <div className={styles.sectionEyebrow}>Planos disponíveis após o beta</div>
        <PlansGrid currentTier={tier} />

        {/* Billing */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardTitle}>Forma de pagamento</span>
            <span className={styles.soonChip}>Em breve</span>
          </div>
          <div className={styles.payBody}>
            <div className={styles.payLogo}>PIX</div>
            <div className={styles.payInfo}>
              <div className={styles.payTitle}>Pagamento via Pix</div>
              <div className={styles.payDesc}>
                Assinaturas e ligas pagas serão cobradas via Pix. A integração chega após o
                período beta — nada a configurar agora.
              </div>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardTitle}>Histórico de cobrança</span>
          </div>
          <div className={styles.invEmpty}>
            Nenhuma cobrança ainda — você está no plano Beta gratuito.
          </div>
        </div>
      </div>
    </>
  )
}
